/**
 * Native share helper — Capacitor Share on Android, Web Share API in
 * browsers that support it, clipboard / download fallback elsewhere.
 *
 * Returns { ok: boolean, method: 'native' | 'web' | 'download' | 'clipboard' | 'error' }
 *
 * Tester complaint v33:
 *   "Share the time should be in the form of a label/pic … not sure if I
 *   want the link to the game when sharing."
 * → We now render a 1080x1080 PNG card (see lib/shareCard.js) and share
 *   THAT as the primary payload. The Play Store URL is NO LONGER appended.
 */
import { Capacitor } from "@capacitor/core";
import { Share } from "@capacitor/share";
import { Filesystem, Directory } from "@capacitor/filesystem";
import { toast } from "sonner";
import { renderShareCard, dataUrlToBlob } from "./shareCard";

/**
 * @param {Object} opts
 * @param {string} [opts.title]
 * @param {string} [opts.text]      Optional caption (NO Play Store link).
 * @param {Object} opts.card        Card payload — see renderShareCard().
 */
export async function shareResult({ title, text, card }) {
  let dataUrl = null;
  try {
    dataUrl = renderShareCard(card);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("shareCard render failed, falling back to text-only", e);
  }

  const captionText = text || "";

  // 1. Native Capacitor Share with file URI (Android / iOS)
  if (Capacitor.isNativePlatform() && dataUrl) {
    try {
      const base64 = dataUrl.split(",")[1];
      const fileName = `fitjong-share-${Date.now()}.png`;
      const written = await Filesystem.writeFile({
        path: fileName,
        data: base64,
        directory: Directory.Cache,
      });
      await Share.share({
        title: title || "Fitjong",
        text: captionText,
        files: [written.uri],
        dialogTitle: "Share your time",
      });
      return { ok: true, method: "native" };
    } catch (err) {
      if (String(err?.message || err).toLowerCase().includes("cancel")) {
        return { ok: false, method: "native" };
      }
      // fall through to text-only native share
      try {
        await Share.share({
          title: title || "Fitjong",
          text: captionText,
          dialogTitle: "Share your time",
        });
        return { ok: true, method: "native" };
      } catch {}
    }
  }

  // 2. Web Share API with files (mobile browsers that support file sharing)
  if (dataUrl && typeof navigator !== "undefined" && typeof navigator.share === "function") {
    try {
      const blob = dataUrlToBlob(dataUrl);
      const file = new File([blob], "fitjong-share.png", { type: "image/png" });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ title: title || "Fitjong", text: captionText, files: [file] });
        return { ok: true, method: "web" };
      }
      // Fallback: share text only
      await navigator.share({ title: title || "Fitjong", text: captionText });
      return { ok: true, method: "web" };
    } catch (err) {
      if (String(err?.name || "").toLowerCase() === "aborterror") {
        return { ok: false, method: "web" };
      }
    }
  }

  // 3. Desktop / unsupported browser fallback — trigger a PNG download
  if (dataUrl && typeof document !== "undefined") {
    try {
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = "fitjong-share.png";
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success("Image downloaded");
      return { ok: true, method: "download" };
    } catch {}
  }

  // 4. Final fallback — clipboard the caption
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(captionText);
      toast.success("Copied to clipboard");
      return { ok: true, method: "clipboard" };
    }
  } catch {}

  toast.error("Couldn't open the share sheet");
  return { ok: false, method: "error" };
}

function fmt(s) {
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, "0")}`;
}

/**
 * Short caption text — used as the share sheet message body. Intentionally
 * does NOT include the Play Store URL: per tester request, the image
 * carries the brand and the link feels spammy when posting to friends.
 */
export function buildShareText({ won, timeSec, coins, calories, levelLabel }) {
  if (won) {
    const where = levelLabel ? ` on ${levelLabel}` : "";
    return `Fitjong${where} · cleared in ${fmt(timeSec)} ⚡ +${coins || 0} coins · ${calories || 0} cal`;
  }
  return `Fitjong${levelLabel ? ` on ${levelLabel}` : ""} · ${calories || 0} cal burned`;
}

// ── Invite / growth loop ──────────────────────────────────────────────
// Unlike shareResult() (which shares an image card and NO link), the
// invite loop intentionally deep-links back to the Play Store listing so
// friends can install the app. Used by ShareCard.
export const FITJONG_PLAY_URL =
  "https://play.google.com/store/apps/details?id=com.fitjong.app";

/**
 * Share a text invite + Play Store link.
 * @returns "shared" | "copied" | "cancelled" | "failed"
 */
export async function shareInvite({ title, text }) {
  const url = FITJONG_PLAY_URL;
  const native = Capacitor?.isNativePlatform?.();
  try {
    await Share.share({ title: title || "Fitjong", text, url, dialogTitle: title || "Fitjong" });
    return "shared";
  } catch (e) {
    if (native) return "cancelled";
    if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
      try {
        await navigator.share({ title: title || "Fitjong", text, url });
        return "shared";
      } catch (_) {
        return "cancelled";
      }
    }
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(`${text} ${url}`);
        return "copied";
      } catch (_) {
        /* fall through */
      }
    }
    return "failed";
  }
}
